import clsx from 'clsx';
import { ArrowUp } from 'lucide-react';
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type KeyboardEvent
} from 'react';
import { useAppParams } from '../api/params';
import { getEffectiveModuleId } from '../api/feature-payload';
import { FEATURE_CATALOG, type FeatureMeta, type Scope } from './features-catalog';
import { PhiaMark } from './PhiaMark';
import { ToolCard } from './ToolCard';
import type { FeatureKey } from '../types';
import styles from './HomeView.module.css';

type Props = {
  onOpenFeature: (key: FeatureKey, payload?: unknown) => void;
};

type ScopeDefinition = {
  scope: Scope;
  title: string;
  shortLabel: string;
  contextName?: string;
};

// One tint per scope so every card inside a scope section reads as the
// same surface. This is louder than the per-activity tints but it makes
// the new grouping crystal-clear at a glance — the colour itself tells
// the user which scope they're operating on.
const SCOPE_TINT: Record<Scope, string> = {
  module: 'var(--tint-amber)',
  section: 'var(--tint-rose)',
  course: 'var(--tint-blue)'
};

export function HomeView({ onOpenFeature }: Props) {
  const [draft, setDraft] = useState('');
  const params = useAppParams();
  const name = params?.user?.name?.trim();

  // Decide which scope sections to show, based on what the host passed
  // at mount() time. A scope is shown ONLY when the IDs needed to build
  // the activity context for that scope are present. Without the IDs,
  // the request would fail server-side — better to hide the buttons.
  //
  // Visibility matrix:
  //   - module  → MOD && UDI && effectiveModuleId (module_id OR last HTM
  //               in `route`) && module_kind !== 'activity'
  //   - section → MOD && UDI
  //   - course  → MOD
  const visibleScopes = useMemo<ScopeDefinition[]>(() => {
    const list: ScopeDefinition[] = [];
    const onActivityModule = params?.module_kind === 'activity';
    const effectiveModuleId = params ? getEffectiveModuleId(params) : null;
    if (params?.MOD && params?.UDI && effectiveModuleId && !onActivityModule) {
      list.push({
        scope: 'module',
        title: 'Sobre el módulo',
        shortLabel: 'este contenido',
        contextName: params?.module_name
      });
    }
    if (params?.MOD && params?.UDI) {
      list.push({
        scope: 'section',
        title: 'Sobre la unidad',
        shortLabel: 'esta unidad',
        contextName: params?.section_name
      });
    }
    if (params?.MOD) {
      list.push({
        scope: 'course',
        title: 'Sobre el curso',
        shortLabel: 'todo el curso',
        contextName: params?.course_name
      });
    }
    return list;
  }, [
    params?.MOD,
    params?.UDI,
    params?.module_id,
    params?.route,
    params?.module_kind,
    params?.module_name,
    params?.section_name,
    params?.course_name
  ]);

  // Activities available for a given scope, in the order declared by the
  // catalog so the visual taxonomy (tint groupings) stays consistent.
  const featuresByScope = useMemo(() => {
    const map = new Map<Scope, FeatureMeta[]>();
    for (const meta of FEATURE_CATALOG) {
      if (!meta.scopes) continue;
      for (const s of meta.scopes) {
        const arr = map.get(s) ?? [];
        arr.push(meta);
        map.set(s, arr);
      }
    }
    return map;
  }, []);

  // Fade at the bottom of the scroller — only visible while there is
  // still content below the fold.
  const scrollRef = useRef<HTMLDivElement>(null);
  const [hasMoreBelow, setHasMoreBelow] = useState(false);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const update = () => {
      const { scrollTop, scrollHeight, clientHeight } = el;
      setHasMoreBelow(scrollTop + clientHeight < scrollHeight - 4);
    };
    update();
    el.addEventListener('scroll', update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', update);
      ro.disconnect();
    };
  }, [visibleScopes.length]);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const text = draft.trim();
    if (!text) return;
    onOpenFeature('threads', { initialMessage: text });
    setDraft('');
  };

  const onKey = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit(e as unknown as FormEvent);
    }
  };

  return (
    <div className={styles.home}>
      <div className={styles.scrollWrap}>
        <div className={styles.scroll} ref={scrollRef}>
          <section className={styles.hero}>
            <h2 className={styles.heroTitle}>
              <span
                className={styles.heroMarkWrap}
                data-tooltip="Hola, soy Phia, tu asistente de estudio."
              >
                <PhiaMark className={styles.heroMark} />
              </span>
              <span>
                {name ? `¡Hola, ${name}! ` : '¡Hola! '}¿Sobre qué quieres estudiar?
              </span>
            </h2>
          </section>

          {visibleScopes.map((def) => {
            const features = featuresByScope.get(def.scope) ?? [];
            if (features.length === 0) return null;
            return (
              <section key={def.scope} className={styles.scopeSection}>
                <header className={styles.scopeHeader}>
                  <h3 className={styles.scopeTitle}>{def.title}</h3>
                  {def.contextName && (
                    <span
                      className={styles.scopePill}
                      style={{ ['--tint' as string]: SCOPE_TINT[def.scope] }}
                      title={def.contextName}
                    >
                      {def.contextName}
                    </span>
                  )}
                </header>
                <div className={styles.grid}>
                  {features.map((meta) => (
                    <ToolCard
                      key={`${def.scope}:${meta.key}`}
                      meta={meta}
                      tint={SCOPE_TINT[def.scope]}
                      onClick={() => onOpenFeature(meta.key, { scope: def.scope })}
                    />
                  ))}
                </div>
              </section>
            );
          })}
        </div>
        <div
          className={clsx(styles.scrollFade, hasMoreBelow && styles.scrollFadeVisible)}
          aria-hidden
        />
      </div>

      <form className={styles.composer} onSubmit={submit}>
        <textarea
          rows={3}
          placeholder="Pregúntame lo que quieras…"
          className={styles.input}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKey}
        />
        <button
          type="submit"
          className={styles.send}
          aria-label="Enviar"
          disabled={!draft.trim()}
        >
          <ArrowUp size={20} strokeWidth={2.6} />
        </button>
      </form>
    </div>
  );
}
